import React, { useCallback, useState } from 'react';

import Permissions from '@/common/permissions';
import { useHasAccess } from '@/modules/core/hooks';

import { CategoryItem, EditServiceData, ServiceItem } from '../../Services.types';
import { ServiceModalContainer } from '../ServiceModal';

import { CategoryCard, HideServiceModal, ServicesList } from './components';
import { useCategoryExpand, useHideServiceModal } from './hooks';
import { useGetServicesByCategoryQuery } from './queries';

interface CategoryCardContainerProps {
  category: CategoryItem;
}

const CategoryCardContainer: React.FC<CategoryCardContainerProps> = ({ category }) => {
  const {
    isExpanded,
    searchString,
    currentSearch,
    serviceTypeFilter,
    page,
    perPage,
    handleToggleExpand,
    handleSearchChange,
    handleServiceTypeChange,
    handlePageChange,
    handlePerPageChange,
  } = useCategoryExpand();

  const { services, isLoading, total } = useGetServicesByCategoryQuery(
    {
      categoryType: category.name,
      name: currentSearch || undefined,
      serviceType: serviceTypeFilter,
      page: page - 1,
      size: perPage,
    },
    isExpanded
  );

  const isEditAccessible = useHasAccess([
    { permission: Permissions.MANAGE_SERVICES },
    { permission: Permissions.SET_UP_PROCESSING_DEFINITION },
  ]);

  const [editData, setEditData] = useState<EditServiceData | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);

  const {
    isVisible: isHideModalVisible,
    isHiding,
    isLoading: isHideLoading,
    serviceName: hideServiceName,
    handleOpenHide,
    handleOpenUnhide,
    handleClose: handleCloseHideModal,
    handleConfirm: handleConfirmHideUnhide,
  } = useHideServiceModal();

  const handleEditService = useCallback((service: ServiceItem) => {
    setEditData({
      id: service.id,
      name: service.name,
      categoryType: service.categoryType,
    });
    setIsEditModalVisible(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalVisible(false);
    setEditData(null);
  }, []);

  return (
    <>
      <CategoryCard category={category} isExpanded={isExpanded} onToggleExpand={handleToggleExpand}>
        <ServicesList
          services={services}
          total={total}
          isLoading={isLoading}
          searchString={searchString}
          serviceTypeFilter={serviceTypeFilter}
          page={page}
          perPage={perPage}
          isEditAccessible={isEditAccessible}
          onSearchChange={handleSearchChange}
          onServiceTypeChange={handleServiceTypeChange}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
          onEditService={handleEditService}
          onHideService={handleOpenHide}
          onUnhideService={handleOpenUnhide}
        />
      </CategoryCard>
      <ServiceModalContainer
        visible={isEditModalVisible}
        onClose={handleCloseEditModal}
        editData={editData}
      />
      <HideServiceModal
        visible={isHideModalVisible}
        serviceName={hideServiceName}
        isHiding={isHiding}
        isLoading={isHideLoading}
        onClose={handleCloseHideModal}
        onConfirm={handleConfirmHideUnhide}
      />
    </>
  );
};

export default CategoryCardContainer;
